/**
 * Die Glocke in der Kopfzeile — der stille Rueckkanal. (Ticket 2331, 17.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Hintergrund-Auftraege werden fertig, ohne dass ich es erfahre —
 * ich musste heute dreimal selbst nachfragen, ob etwas fertig ist. … Eine
 * Glocke in der Kopfzeile, der STILLE Weg. Kein Ton, kein Push, kein
 * Aufwecken. Nur ein Punkt an der Glocke, wenn etwas hinterlegt ist."*
 *
 * ## Zwei Dinge, die vor der ersten Zeile Code feststanden
 *
 * ⚠️ **Gesammelt wird seit Ticket 273.** `handover_notes` trug beim Bauen
 *    168 Eintraege, ALLE als verbraucht markiert — jeder still in einen
 *    Chat-Kontext gewandert und dort verschwunden. Gefehlt hat nie das
 *    Sammeln, sondern der Weg zurueck zum Menschen. Deshalb legt dieses
 *    Modul nichts Neues an; es zeigt, was da ist.
 *
 * ⚠️ **Es gab hier schon einmal eine Glocke.** Sie flog am 14.07. aus der
 *    Kopfzeile und am 15.09. ganz weg. Der Unterschied steht in Martins
 *    eigener Regel vom 16.09.:
 *
 *      *„fuer dich ist etwas da" gehoert in die Kopfleiste,
 *       „du schuldest etwas" nicht.*
 *
 *    Die alte zeigte offene Aufgaben — Schuld. Diese zeigt fertige Arbeit.
 *    Wer sie je um Aufgaben, Erinnerungen oder Ungelesenes erweitert, macht
 *    aus der Einladung wieder eine Mahnung und bringt sie zurueck um.
 *
 * ## Die einfache Glocke (06.10.2026)
 *
 * Martin: *„Weiß ich noch nicht so genau, was ich mit dem Glockensymbol für
 * dich hinterlegt machen soll … sehr viele Informationen, die ich nicht
 * wirklich verwerten kann.“* Seitdem ordnet der Server in „Für dich“,
 * „Fertig“ und „Routine“ (shared/glocke-ordnung.ts) und zählt nur Neues der
 * ersten beiden. Die Karte zeigt oben, was neu ist; was früher kam, liegt
 * eingeklappt darunter, die Routine ganz unten, ebenfalls eingeklappt.
 * Antwortet der Server ohne `einfach` (Schalter GLOCKE_EINFACH=0), zeichnet
 * dieses Modul die alte Liste — unverändert.
 *
 * ## Die Karte in drei Antworten (07.10.2026)
 *
 * Martin: *„Das ist unbedienbar. … ich habe unabsichtlich auch ‚Alles schließen‘
 * … ich weiß ehrlich gesagt nicht, was ich mit der Liste anfangen soll.“* Die
 * Karte (frontend/lib/glocke-karte.ts) sagt nur noch: was braucht dich, was ist
 * fertig, was ist Routine. Der Punkt am Knopf zählt allein „Braucht dich“.
 *
 * „Alles als gelesen markieren“ steht klein am Ende, nicht neben den Zeilen, und
 * danach zehn Sekunden lang „Alles gelesen. Rückgängig“. Zurückgenommen wird beim
 * SERVER (POST /api/glocke/zurueck, dieselbe Spalte `gesehen_at`) mit genau den ids,
 * die das Abhaken zurückgemeldet hat — der Browser merkt sich nur diese Liste für
 * die zehn Sekunden und hält sonst keinen Zustand, der zählt. Nach jeder Änderung
 * wird die Karte neu geholt: der Server zählt, der Browser nie.
 *
 * ## Ehrliche Sätze (07.10.2026, Prüfbefund „Anzeige behauptet zu viel“)
 *
 * Die Statuszeile sagt nur, was der Server wirklich getan hat: „Alles gelesen.“ nur, wenn er mindestens einen
 * Hinweis umgeschaltet hat (`ids.length > 0`), „Wieder offen.“ nur bei `zurueck > 0`. Hat er nichts geändert,
 * steht „Nichts zu ändern.“; ging die Anfrage schief (500, offline), „Das hat nicht geklappt.“ — und bei
 * „Rückgängig“ bleibt der Knopf stehen, damit man es noch einmal versuchen kann.
 */
import { relativeZeit } from "./relative-time.js";
import { icon } from "./icons.js";
import { kuerzeFuerAnzeige, ohneLaufMarker } from "../../shared/html-text";
import { quelleInWorten } from "../../shared/glocke-ordnung";
import { zeichneKarte, type KartenAktionen, type KartenAntwort } from "./glocke-karte.js";
import { lageDa, LAGE_GEAENDERT } from "./lage-fenster.js";
import { baueLeiste, NOCHMAL_MINDEST_MS, RUECKGAENGIG_MS } from "./glocke-leiste.js";

/** Ein Hinweis der alten, flachen Liste (Schalter GLOCKE_EINFACH=0). */
export interface GlockenEintrag {
  id: number;
  source: string;
  title: string;
  summary: string;
  createdAt: string;
  gesehen: boolean;
}

/** Was /api/glocke antwortet: die alte Liste (`eintraege`) oder die einfache Karte (`einfach`). */
type GlockenAntwort = Partial<KartenAntwort> & {
  ok?: boolean;
  ungesehen?: number;
  /** Nur die alte Glocke: die neuesten 50. */
  eintraege?: GlockenEintrag[];
};

/**
 * Ab so vielen Hinweisen steht „9+“ statt der Zahl. (Martins Bild vom 05.10.:
 * „9+“.) Der Deckel ist Absicht: die Glocke zählt nicht auf, sie lädt ein —
 * eine genaue „43“ wäre wieder der Druck, den Ticket 2331 ausgeschlossen hat.
 * Die Plakette in der Kopfleiste ist trotzdem für „99+“ gebaut (28 Punkte bei
 * 44 Breite), falls der Deckel je steigt.
 */
export const GLOCKE_ZAHL_GRENZE = 9;

/** Was in der Plakette steht: die Zahl, ab der Grenze „9+“. */
export function glockenZahlText(ungesehen: number): string {
  return ungesehen > GLOCKE_ZAHL_GRENZE ? `${GLOCKE_ZAHL_GRENZE}+` : String(ungesehen);
}

/**
 * Der Satz für Vorleser: die Plakette zeigt „9+“, das Label nennt die echte Zahl.
 * (Sie steht im aria-label und nicht im sichtbaren Text, damit der Deckel nur
 * den Blick beruhigt, nicht die Auskunft verkürzt.)
 */
export function glockenSatz(ungesehen: number): string {
  return ungesehen === 1
    ? "Ein Hinweis liegt für dich bereit"
    : `${ungesehen} Hinweise liegen für dich bereit`;
}

/**
 * Derselbe Satz für die einfache Glocke: die echten Zahlen je Antwort.
 * „Hinweise – neu: 2 brauchen dich, 5 fertig; Routine: 8 Berichte“.
 */
function glockenSatzJeGruppe(
  zahl: { braucht: number; fertig: number },
  routine: number,
): string {
  const neu =
    zahl.braucht + zahl.fertig === 0
      ? "nichts Neues"
      : `neu: ${zahl.braucht} ${zahl.braucht === 1 ? "braucht" : "brauchen"} dich, ${zahl.fertig} fertig`;
  const rest =
    routine === 0 ? "" : `; Routine: ${routine === 1 ? "ein Bericht" : `${routine} Berichte`}`;
  return `Hinweise – ${neu}${rest}`;
}

/** Die Zeile im Fenster „Lage“, die in die Liste führt: „9 neue Meldungen ansehen“. */
function meldungenSatz(ungesehen: number): string {
  if (ungesehen <= 0) return "Meldungen ansehen";
  return ungesehen === 1 ? "1 neue Meldung ansehen" : `${ungesehen} neue Meldungen ansehen`;
}

/**
 * Das Zeichen im Kopf setzen.
 *
 * Martin: *„Punkt bei ungelesen, Zahl bei mehreren."* — bei genau einem
 * bleibt es beim Punkt; eine „1" waere eine Zahl, die nichts zaehlt.
 *
 * Die einfache Glocke zählt nur „Braucht dich“ (07.10.2026): `ungesehen` ist die Zahl dieser
 * Zeilen. Fertiges (außer einer Webseite, deren Vorschau Martin ansehen soll — die steht in
 * „Braucht dich“) und Routine setzen keinen Punkt — aber die Glocke bleibt im Kopf,
 * solange in der Karte etwas liegt, leise und ohne Farbe. Sonst wäre das Fertige nicht
 * mehr zu erreichen, sobald nichts mehr dich braucht.
 */
/** Lag bei der letzten Antwort etwas in der Liste? Die Glocke braucht es, wenn nur ein Hinweis der Lage kommt oder geht. */
let liegtEtwasLetzte = false;

/**
 * Ein Hinweis der Lage (Spiegel, Mac, Aufgaben) ist erschienen oder weggegangen: die Glocke
 * zeigt sich neu, OHNE neu zu fragen. `hidden` der Glocke gehört weiter allein diesem Modul.
 */
document.addEventListener(LAGE_GEAENDERT, () => {
  const knopf = document.getElementById("topbar-glocke");
  if (knopf) knopf.hidden = !(liegtEtwasLetzte || lageDa());
});

function zeichneZahl(d: GlockenAntwort): void {
  const ungesehen = Number(d.ungesehen ?? 0);
  const knopf = document.getElementById("topbar-glocke");
  const zahl = document.getElementById("topbar-glocke-zahl");
  if (!knopf) return;
  const inDerKarte = (d.fertig?.length ?? 0) + (d.brauchtFrueher?.length ?? 0) + (d.routine?.length ?? 0);
  const liegtEtwas = d.einfach ? ungesehen > 0 || inDerKarte > 0 : ungesehen > 0;
  // Die Lage (Fenster unter dem Kopf: Spiegel, Mac, Aufgaben) hängt an der Glocke: sie bleibt auch ohne Meldungen erreichbar.
  liegtEtwasLetzte = liegtEtwas;
  knopf.hidden = !(liegtEtwas || lageDa());
  const ansehen = document.getElementById("topbar-lage-meldungen");
  if (ansehen) {
    ansehen.hidden = !liegtEtwas;
    ansehen.textContent = meldungenSatz(ungesehen);
  }
  knopf.classList.toggle("topbar-glocke--voll", ungesehen > 0);
  if (!zahl) return;
  zahl.hidden = ungesehen < 2;
  zahl.textContent = glockenZahlText(ungesehen);
  knopf.setAttribute(
    "aria-label",
    d.einfach
      ? glockenSatzJeGruppe(d.zahl ?? { braucht: ungesehen, fertig: 0 }, d.routine?.length ?? 0)
      : glockenSatz(ungesehen),
  );
}

async function holeGlocke(): Promise<GlockenAntwort | null> {
  const r = await fetch("/api/glocke", { credentials: "same-origin" });
  return r.ok ? ((await r.json()) as GlockenAntwort) : null;
}

/**
 * Fragen, ob etwas hinterlegt ist.
 *
 * ⚠️ Kein Dauerpuls. Beim Start und nach jedem Abhaken — mehr braucht ein
 *    Kanal nicht, der ausdruecklich warten darf. Ein Intervall waere genau
 *    die Unruhe, die Martin mit „kein Ton, kein Push" ausgeschlossen hat.
 */
export function pruefeGlocke(): void {
  void holeGlocke()
    .then((d) => {
      if (!d?.ok) return;
      zeichneZahl(d);
    })
    .catch(() => {
      /* Eine Kopfleiste darf an dieser Frage nie scheitern. */
    });
}

/**
 * Abhaken. Gibt die Hinweise zurück, die dabei wirklich umgeschaltet wurden — nur sie kann
 * „Rückgängig“ wieder aufmachen — oder null, wenn es nicht ging. Danach fragt der Aufrufer neu:
 * er zählt nicht, der Server zählt (die Zahl hängt an Gruppe und Alter, die kennt nur er).
 *
 * `keepalive`: „Ansehen“ ist ein Link und verlässt die Seite — die Anfrage muss das überleben.
 */
async function hakeAb(ids?: number[]): Promise<number[] | null> {
  try {
    const r = await fetch("/api/glocke/gesehen", {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(ids ? { ids } : {}),
    });
    if (!r.ok) return null;
    const d = (await r.json().catch(() => ({}))) as { ids?: unknown };
    return Array.isArray(d.ids) ? d.ids.filter((n): n is number => Number.isInteger(n)) : [];
  } catch {
    return null;
  }
}

/**
 * Das Abhaken von eben zurücknehmen — beim Server, mit genau den ids, die es gemeldet hat. Gibt zurück, wie viele
 * Hinweise er wirklich wieder aufgemacht hat (0 = nichts mehr zu ändern, etwa weil die Frist um ist), oder null,
 * wenn es nicht ging (Fehler, kein Netz).
 */
async function nimmZurueck(ids: number[]): Promise<number | null> {
  try {
    const r = await fetch("/api/glocke/zurueck", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
    if (!r.ok) return null;
    const d = (await r.json().catch(() => ({}))) as { zurueck?: unknown };
    return typeof d.zurueck === "number" && Number.isInteger(d.zurueck) && d.zurueck > 0 ? d.zurueck : 0;
  } catch {
    return null;
  }
}

/** Titel, Zusammenfassung und die kleine Zeile darunter — für die alte Liste. */
function baueText(e: GlockenEintrag): HTMLSpanElement {
  const text = document.createElement("span");
  text.className = "glocke-text";
  const titel = document.createElement("strong");
  titel.className = "glocke-titel";
  titel.textContent = e.title;
  text.appendChild(titel);
  // 18.09.2026: beides war Martins Foto. Der Text endete mitten im Wort
  // („… bei Mikrofon-Starts oder"), weil hier gar nicht gekuerzt wurde —
  // die CSS-Zeilenklemme schnitt einfach ab. Und er begann mit dem
  // Maschinen-Marker „TREFFER: ". kuerzeAufSatz gibt es seit dem 04.09.
  // in shared/, sie wurde hier nur nie gefragt.
  const zusammenfassung = kuerzeFuerAnzeige(ohneLaufMarker(e.summary), 180);
  if (zusammenfassung) {
    const zusammen = document.createElement("span");
    zusammen.className = "glocke-zusammenfassung";
    zusammen.textContent = zusammenfassung;
    text.appendChild(zusammen);
  }
  const meta = document.createElement("span");
  meta.className = "glocke-meta";
  meta.textContent = [quelleInWorten(e.source), relativeZeit(e.createdAt)].filter(Boolean).join(" · ");
  text.appendChild(meta);
  return text;
}

/** Eine Zeile der alten Liste. Antippen hakt sie ab, still und an Ort und Stelle. */
function baueAlteZeile(e: GlockenEintrag): HTMLLIElement {
  const li = document.createElement("li");
  const knopf = document.createElement("button");
  knopf.type = "button";
  knopf.className =
    "glocke-zeile" + (e.gesehen ? " glocke-zeile--gesehen" : "");

  const punkt = document.createElement("span");
  punkt.className = "glocke-punkt";
  punkt.setAttribute("aria-hidden", "true");
  knopf.appendChild(punkt);
  knopf.appendChild(baueText(e));

  knopf.addEventListener("click", () => {
    if (knopf.classList.contains("glocke-zeile--gesehen")) return;
    // Sofort ruhig stellen — die Antwort vom Server bestaetigt nur.
    knopf.classList.add("glocke-zeile--gesehen");
    void hakeAb([e.id]).then(() => pruefeGlocke());
  });
  li.appendChild(knopf);
  return li;
}

function leererSatz(satz: string): HTMLParagraphElement {
  const p = document.createElement("p");
  p.className = "glocke-leer";
  p.textContent = satz;
  return p;
}

/** Die alte Liste (Schalter GLOCKE_EINFACH=0): die neuesten 50, flach. */
function fuelleAlt(inhalt: HTMLElement, d: GlockenAntwort): void {
  const eintraege = d.eintraege ?? [];
  inhalt.replaceChildren();
  if (eintraege.length === 0) {
    inhalt.appendChild(leererSatz("Nichts hinterlegt."));
    return;
  }
  const ul = document.createElement("ul");
  ul.className = "glocke-liste";
  ul.append(...eintraege.map(baueAlteZeile));
  inhalt.appendChild(ul);

  if (eintraege.some((e) => !e.gesehen)) {
    const fuss = document.createElement("div");
    fuss.className = "glocke-fuss";
    const alle = document.createElement("button");
    alle.type = "button";
    alle.className = "btn btn--ghost btn--sm";
    alle.textContent = "Alle abhaken";
    alle.addEventListener("click", () => {
      ul.querySelectorAll(".glocke-zeile").forEach((z) => z.classList.add("glocke-zeile--gesehen"));
      alle.remove();
      void hakeAb().then(() => pruefeGlocke());
    });
    fuss.appendChild(alle);
    inhalt.appendChild(fuss);
  }
}

/** Was die Seite der Karte mitgibt, damit dieses Modul klein bleibt (und ohne die Alltag-Ansicht prüfbar). */
interface GlockeOptionen {
  /**
   * „Benennen“: die Stimmen öffnen. Ohne Angabe: „Sprecher verwalten“ — derselbe Weg wie der Menüeintrag in „Mein Alltag“.
   * `schluessel` ist die Frage der Glocke („stimme-0cc81939“): die Liste stellt genau diese Stimme nach oben.
   */
  benenneStimmen?: (schluessel?: string) => void;
}

/** Die Ansicht, in der man eine fremde Stimme benennt: „Mein Alltag“, Menü ⋯, „Sprecher verwalten“. */
function oeffneSprecher(schluessel?: string): void {
  void import("./alltag-view.js").then((a) => a.openSprecherSheet(schluessel ? { frage: schluessel } : {}));
}

/**
 * Die Karte öffnen. Nutzt die Sheet-Bausteine, nicht ein zweites System.
 *
 * Tastatur: der Fokus zieht in die Karte, Tab bleibt darin, Escape schließt, danach steht der Fokus
 * wieder auf der Glocke. Zum Vorlesen gibt es eine Statuszeile (role="status"), die ansagt, was das
 * Abhaken getan hat.
 */
export function oeffneGlocke(opt: GlockeOptionen = {}): void {
  document.querySelector(".glocke-sheet")?.remove();
  const glocke = document.getElementById("topbar-glocke");

  const el = document.createElement("div");
  el.className = "sheet sheet--modal sheet--menu glocke-sheet";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("aria-label", "Hinweise");

  const karte = document.createElement("div");
  karte.className = "sheet-card glocke-karte";
  karte.tabIndex = -1;
  const kopf = document.createElement("div");
  kopf.className = "glocke-kopf";
  const h = document.createElement("h2");
  h.className = "sheet-title";
  h.textContent = "Hinweise";
  const zu = document.createElement("button");
  zu.type = "button";
  zu.className = "glocke-schliessen";
  zu.setAttribute("aria-label", "Hinweise schließen");
  zu.appendChild(icon("x", { size: 20, stroke: 1.6 }));
  kopf.append(h, zu);
  // Ein Satz, wozu die Karte da ist — nur bei der einfachen Karte (die alte Liste sagt es nicht).
  const erklaerung = document.createElement("p");
  erklaerung.className = "glocke-erklaerung";
  erklaerung.textContent = "Hier steht nur, was dich braucht oder fertig geworden ist.";
  erklaerung.hidden = true;
  const inhalt = document.createElement("div");
  inhalt.className = "glocke-inhalt";
  const leiste = baueLeiste(karte);
  karte.append(kopf, erklaerung, inhalt, leiste.element);

  const grund = document.createElement("div");
  grund.className = "sheet-backdrop";
  el.append(grund, karte);

  const aufgeklappt = new Set<string>();

  const schliessen = (): void => {
    leiste.halte();
    el.remove();
    window.removeEventListener("keydown", aufTaste);
    glocke?.setAttribute("aria-expanded", "false");
    glocke?.focus();
  };
  const aufTaste = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape") {
      schliessen();
      return;
    }
    if (ev.key !== "Tab") return;
    // Tab bleibt in der Karte: das Fenster ist modal, dahinter wartet nichts.
    const ziele = [...el.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]")].filter((n) => !n.closest("[hidden]"));
    const erstes = ziele[0];
    const letztes = ziele[ziele.length - 1];
    if (!erstes || !letztes) {
      ev.preventDefault();
      return;
    }
    const aktiv = document.activeElement;
    if (ev.shiftKey && (aktiv === erstes || aktiv === karte || !el.contains(aktiv))) {
      ev.preventDefault();
      letztes.focus();
    } else if (!ev.shiftKey && (aktiv === letztes || !el.contains(aktiv))) {
      ev.preventDefault();
      erstes.focus();
    }
  };
  grund.addEventListener("click", schliessen);
  zu.addEventListener("click", schliessen);
  // Die feine Linie unter dem klebenden Kopf zeigt sich erst, wenn die Liste darunter durchläuft.
  karte.addEventListener("scroll", () => kopf.classList.toggle("glocke-kopf--linie", karte.scrollTop > 0), { passive: true });

  const sage = leiste.sage;

  /** Neu holen und zeichnen — Zeichen im Kopf und Karte aus EINER Antwort. */
  const lade = async (): Promise<void> => {
    const d = await holeGlocke();
    if (!d?.ok) throw new Error("glocke");
    zeichneZahl(d);
    if (!el.isConnected) return;
    const oben = karte.scrollTop;
    erklaerung.hidden = !d.einfach;
    if (d.einfach) zeichneKarte(inhalt, d as KartenAntwort, aktionen, aufgeklappt);
    else fuelleAlt(inhalt, d);
    karte.scrollTop = oben;
  };
  const ladeStill = (): Promise<void> => lade().catch(() => undefined);

  /** Die Zeile, auf der der Fokus stand, ist weg: er geht zur nächsten Handlung oder zur Überschrift. */
  const fokusNeu = (): void => {
    const ziel = inhalt.querySelector<HTMLElement>(".glocke-handlung") ?? inhalt.querySelector<HTMLElement>(".glocke-gruppe-titel");
    ziel?.focus({ preventScroll: true });
  };

  /**
   * Das Abhaken zurücknehmen. Geht es schief, bleibt der Knopf stehen — bis das Fenster um ist, mindestens aber so
   * lange, dass man den Satz lesen und noch einmal tippen kann (der Server nimmt es zwei Minuten lang zurück).
   */
  const macheRueckgaengig = async (ids: number[], bis: number): Promise<void> => {
    const n = await nimmZurueck(ids);
    if (n === null) {
      sage(
        "Das hat nicht geklappt.",
        { wort: "Rückgängig", tut: () => void macheRueckgaengig(ids, bis) },
        Math.max(bis - Date.now(), NOCHMAL_MINDEST_MS),
      );
      return;
    }
    sage(n > 0 ? "Wieder offen." : "Nichts zu ändern.", undefined, 3_000);
    await ladeStill();
  };

  const aktionen: KartenAktionen = {
    handle: (zeile, knopf) => {
      if (zeile.handlung?.ziel === "stimmen") {
        // Nur hinführen: der Hinweis gilt erst als gelesen, wenn die Stimme einen Namen hat (server/index.ts, speakers/name).
        schliessen();
        (opt.benenneStimmen ?? oeffneSprecher)(zeile.handlung.schluessel);
        return;
      }
      // Ein Link verlässt die Seite selbst; hier wird nur noch abgehakt (die Anfrage überlebt das).
      if (zeile.handlung?.href) {
        void hakeAb(zeile.ids);
        return;
      }
      (knopf as HTMLButtonElement).disabled = true;
      void hakeAb(zeile.ids).then(async (ids) => {
        if (ids === null) {
          (knopf as HTMLButtonElement).disabled = false;
          sage("Das hat nicht geklappt.");
          return;
        }
        await ladeStill();
        fokusNeu();
      });
    },
    alles: (offen, knopf) => {
      (knopf as HTMLButtonElement).disabled = true;
      void hakeAb(offen).then(async (ids) => {
        if (ids === null) {
          (knopf as HTMLButtonElement).disabled = false;
          sage("Das hat nicht geklappt.");
          return;
        }
        // Erst sagen, dann neu zeichnen: der Fokus geht auf „Rückgängig“ und bleibt dort. Und nur sagen, was geschah:
        // hat der Server nichts umgeschaltet, gibt es nichts zurückzunehmen.
        if (ids.length > 0) {
          const bis = Date.now() + RUECKGAENGIG_MS;
          sage("Alles gelesen.", { wort: "Rückgängig", tut: () => void macheRueckgaengig(ids, bis) });
        } else {
          sage("Nichts zu ändern.", undefined, 3_000);
        }
        await ladeStill();
      });
    },
  };

  document.body.appendChild(el);
  window.addEventListener("keydown", aufTaste);
  glocke?.setAttribute("aria-expanded", "true");
  karte.focus({ preventScroll: true });

  lade().catch(() => {
    inhalt.replaceChildren(leererSatz("Die Liste ließ sich gerade nicht laden."));
  });
}
